import { useCallback } from "react";
import { useSelector } from "react-redux";
import { selectActiveFy } from "../app/financialYearSlice";

// Safe date parser: handles DD/MMM/YYYY, DD/MM/YYYY, YYYY-MM-DD, ISO etc.
export const parseDateToYyyyMmDd = (dateStr, fallback = "") => {
  if (!dateStr) return fallback;
  const cleanStr = String(dateStr).trim().split(/\s+|T(?=\d{2}:)/)[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleanStr)) return cleanStr;
  const parts = cleanStr.split(/[-/]/);
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const year = parseInt(parts[2], 10);
    if (year > 1000) {
      let month = parseInt(parts[1], 10);
      if (isNaN(month)) {
        const abbr = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
        month = abbr.indexOf(parts[1].toLowerCase()) + 1;
      }
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31)
        return `${year}-${String(month).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    }
  }
  const d = new Date(cleanStr);
  if (!isNaN(d.getTime()))
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  return fallback;
};

/**
 * Checks if a given date string is within the active financial year limits.
 * Returns true if valid, false otherwise.
 */
export const isDateInFy = (dateVal, activeFy) => {
  if (!activeFy || !activeFy.fromDate || !activeFy.toDate) return true; // fallback if no FY loaded
  if (!dateVal) return true;
  const parsed = parseDateToYyyyMmDd(dateVal);
  if (!parsed) return true;

  // If year is not a complete 4-digit year (e.g. while typing in browser date input: 0002, 0026),
  // allow it so typing is not interrupted by premature rejection
  const year = parseInt(parsed.split("-")[0], 10);
  if (year < 1000) return true;

  const checkDate = new Date(parsed);
  const fromDate = new Date(parseDateToYyyyMmDd(activeFy.fromDate));
  const toDate = new Date(parseDateToYyyyMmDd(activeFy.toDate));
  
  if (isNaN(checkDate.getTime()) || isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) return true;
  return checkDate >= fromDate && checkDate <= toDate;
};

/**
 * Custom React Hook to retrieve current financial year date boundaries
 * to use in min/max input attributes and validate user selections.
 */
export const useActiveFyLimits = () => {
  const activeFy = useSelector(selectActiveFy);
  
  const minDate = activeFy?.fromDate ? parseDateToYyyyMmDd(activeFy.fromDate) : "";
  const maxDate = activeFy?.toDate ? parseDateToYyyyMmDd(activeFy.toDate) : "";
  
  const validateAndAlert = useCallback((dateVal, fallbackDate = "") => {
    if (dateVal && !isDateInFy(dateVal, activeFy)) {
      alert("Date incompatible with financial year");
      return false;
    }
    return true;
  }, [activeFy]);

  const checkDateInFy = useCallback((dateVal) => isDateInFy(dateVal, activeFy), [activeFy]);

  return {
    minDate,
    maxDate,
    activeFy,
    validateAndAlert,
    isDateInFy: checkDateInFy
  };
};
